/**
 * Sync job type enums and interfaces.
 *
 * These types are used across:
 * - Database schema (adapter_sync_jobs table)
 * - Event payloads (PERSON_SYNC_REQUESTED, etc.)
 * - API responses (GET /persons/sync/{jobId})
 * - Frontend UI components
 */

/**
 * Sync job status - lifecycle progression.
 */
export enum SyncJobStatus {
	/** Job created, queued for processing */
	ACCEPTED = 'accepted',
	/** Job is currently running */
	RUNNING = 'running',
	/** Job completed successfully */
	COMPLETED = 'completed',
	/** Job failed with error */
	FAILED = 'failed',
}

/**
 * Who triggered the sync job.
 */
export enum SyncJobTriggeredBy {
	/** System-triggered (cron job, scheduled sync) */
	SYSTEM = 'system',
	/** User-triggered (manual API call) - value will be userId */
}

/**
 * Event source type - where the sync was initiated.
 */
export enum SyncEventSource {
	/** Triggered via HTTP API endpoint (manual) */
	API = 'api',
	/** Triggered via cron job (scheduled) */
	CRON = 'cron',
	/** Triggered by worker processing */
	WORKER = 'worker',
}

/**
 * Type of sync job (discriminator across multiple providers).
 *
 * `EXTERNAL_TICKETS` — periodic poll over open tickets in the
 * `tickets` table to reconcile their state against the source-of-truth
 * external system (today: Productive; tomorrow: Jira / Linear via the
 * `tickets.external_system` discriminator). See Spec 3 Group 8.
 */
export enum SyncJobType {
	/** Microsoft Entra ID sync */
	ENTRA_ID = 'entra_id',
	/** External ticketing-system sync (Productive, Jira, Linear, …) */
	EXTERNAL_TICKETS = 'external_tickets',
}

/**
 * Sync job summary - Entra-shaped result counts. Stored verbatim in
 * `sync_jobs.summary` jsonb when `type = ENTRA_ID`.
 */
export interface SyncJobSummary {
	/** Number of persons created */
	created: number
	/** Number of persons updated */
	updated: number
	/** Number of persons disabled */
	disabled: number
	/** Number of persons reactivated (optional) */
	reactivated?: number
	/** Number of persons marked for pending deletion (optional) */
	pendingDeletion?: number
	/** Number of persons soft-deleted */
	deleted: number
	/** Number of errors encountered */
	errors: number
	/** Error messages (if any) */
	errorMessages?: string[]
}

/**
 * Tickets sync summary — what the polling executor wrote vs. failed
 * to write per run. Stored in `ticket_sync_jobs.summary` jsonb. Spec
 * 3 Group 8.
 */
export interface TicketsSyncJobSummary {
	/** Total rows the executor pulled from `tickets WHERE status != 'closed' AND external_ticket_id IS NOT NULL`. */
	total: number
	/** Rows where the GET succeeded + diff + writeback all completed. */
	succeeded: number
	/** Rows where the per-row try/catch swallowed an error. The next
	 *  cron retries them automatically (their `last_synced_at` doesn't
	 *  advance on failure). */
	failed: number
	/** Total `TICKET_*` events emitted across all rows this run. */
	eventsEmitted: number
	/** First N error messages (for ops triage). The full set is in CloudWatch. */
	errorMessages?: string[]
}
